import { buildApiUrl } from '../config/environment';

// Types for authentication
export interface User {
  id: number;
  username: string;
  displayName?: string;
}

export interface LoginForm {
  username: string;
  password: string;
}

export interface RegisterForm {
  username: string;
  password: string;
  confirmPassword: string;
  couponCode: string;
  displayName: string;
}

export interface CouponValidation {
  isValid: boolean | null;
  message: string;
  monthsFree?: number;
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  error?: string;
}

export interface AuthStatusResponse {
  authenticated: boolean;
  user?: User;
}

// Authentication service functions
export const authService = {
  // Check current authentication status
  checkAuthStatus: async (): Promise<AuthStatusResponse> => {
    try {
      const response = await fetch(buildApiUrl('/api/auth/status'), {
        credentials: 'include'
      });
      const data = await response.json();
      console.log('🔍 Auth status response:', data);
      return {
        authenticated: data.authenticated,
        user: data.user
      };
    } catch (error) {
      console.error('Failed to check auth status:', error);
      return { authenticated: false };
    }
  },

  // Handle user login
  login: async (loginForm: LoginForm): Promise<AuthResponse> => {
    try {
      const response = await fetch(buildApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(loginForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        console.log('🔍 Login response:', data);
        return {
          success: true,
          user: data.user
        };
      } else {
        return {
          success: false,
          error: data.error
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Login failed. Please try again.'
      };
    }
  },

  // Handle user registration
  register: async (registerForm: RegisterForm, couponValidation: CouponValidation): Promise<AuthResponse> => {
    // Validate passwords match
    if (registerForm.password !== registerForm.confirmPassword) {
      return {
        success: false,
        error: 'Passwords do not match'
      };
    }
    
    // Validate coupon if provided
    if (registerForm.couponCode.trim() && couponValidation.isValid !== true) {
      return {
        success: false,
        error: 'Please enter a valid coupon code or remove it'
      };
    }
    
    try {
      const response = await fetch(buildApiUrl('/api/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: registerForm.username,
          password: registerForm.password,
          displayName: registerForm.displayName.trim() || null,
          couponCode: registerForm.couponCode.trim() || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        return {
          success: true,
          user: data.user
        };
      } else {
        return {
          success: false,
          error: data.error
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Registration failed. Please try again.'
      };
    }
  },

  // Handle user logout
  logout: async (): Promise<void> => {
    try {
      await fetch(buildApiUrl('/api/auth/logout'), {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  },

  // Check disclaimer acceptance status
  checkDisclaimerStatus: async (): Promise<{ hasAccepted: boolean }> => {
    try {
      const response = await fetch(buildApiUrl('/api/disclaimer/status'), {
        credentials: 'include'
      });
      const data = await response.json();
      return { hasAccepted: data.hasAccepted };
    } catch (error) {
      console.error('Failed to check disclaimer status:', error);
      return { hasAccepted: false };
    }
  },

  // Accept disclaimer
  acceptDisclaimer: async (doNotShowAgain: boolean): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(buildApiUrl('/api/disclaimer/accept'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ doNotShowAgain })
      });
      
      if (response.ok) {
        return { success: true };
      } else {
        const data = await response.json();
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: 'Failed to accept disclaimer' };
    }
  },

  // Validate coupon code
  validateCoupon: async (code: string): Promise<CouponValidation> => {
    if (!code.trim()) {
      return { isValid: null, message: '' };
    }
    
    try {
      const response = await fetch(buildApiUrl('/api/coupons/validate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: code.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        return {
          isValid: data.valid,
          message: data.valid ? `✅ Valid coupon! ${data.monthsFree} months free` : '❌ Invalid coupon code',
          monthsFree: data.monthsFree
        };
      } else {
        return {
          isValid: false,
          message: '❌ Invalid coupon code'
        };
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      return {
        isValid: false,
        message: '❌ Error validating coupon'
      };
    }
  }
};